import { Texture } from '@mui/icons-material';
import { Box, ClickAwayListener, Modal } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { EActiveDrawer, ESubMenu, EToolbarButton } from '../../../../@types/Enums';
import { IconCatalog, IconCreateElement } from '../../../../assets/icons';
import { primaryIconActive, primaryIconInactive, secondaryIconHover } from '../../../../utils/colors';
import ImsIcon from '../../ImsIcon';

interface ExtendModalProps {
  open: boolean;
  onClose: () => void;
  sizes: any;
  handleToolbarButtonPress: (buttonId: EToolbarButton, subMenuType?: ESubMenu, activatedDrawer?: EActiveDrawer) => void;
  activeDrawer: EActiveDrawer;
  setActiveToolbarButton: (button: EToolbarButton | null) => void;
  buttonPosition?: DOMRect;
}

interface Option {
  id: EToolbarButton;
  text: string;
  icon: any;
  drawer: EActiveDrawer;
  action: () => void;
}

const menuIconStyle = {
  width: '2.25rem',
  height: '2.25rem',
  margin: '.1rem 1rem',
  transition: '.2s ease-in-out',
};

const ExtendModal: React.FC<ExtendModalProps> = ({ open, onClose, sizes, handleToolbarButtonPress, activeDrawer, setActiveToolbarButton, buttonPosition }) => {
  const intl = useIntl();

  const options: Option[] = [
    {
      id: EToolbarButton.ROOM_COMPONENTS,
      text: intl.formatMessage({ id: 'Toolbar.roomComponents' }),
      icon: IconCatalog,
      drawer: EActiveDrawer.ROOM_COMPONENTS,
      action: () => {
        handleToolbarButtonPress(EToolbarButton.ROOM_COMPONENTS, ESubMenu.ROOM_COMPONENTS, EActiveDrawer.ROOM_COMPONENTS);
        setActiveToolbarButton(EToolbarButton.EXTEND);
        onClose();
      },
    },
    {
      id: EToolbarButton.CREATE_ELEMENTS,
      text: intl.formatMessage({ id: 'Toolbar.createElement' }),
      icon: IconCreateElement,
      drawer: EActiveDrawer.CREATE_ELEMENTS,
      action: () => {
        handleToolbarButtonPress(EToolbarButton.CREATE_ELEMENTS, ESubMenu.CREATE_ELEMENTS, EActiveDrawer.CREATE_ELEMENTS);
        setActiveToolbarButton(EToolbarButton.EXTEND);
        onClose();
      },
    },
    {
      id: EToolbarButton.TEXTURES,
      text: intl.formatMessage({ id: 'TextureDrawer.header' }),
      icon: Texture,
      drawer: EActiveDrawer.TEXTURES,
      action: () => {
        handleToolbarButtonPress(EToolbarButton.TEXTURES, ESubMenu.TEXTURES, EActiveDrawer.TEXTURES);
        setActiveToolbarButton(EToolbarButton.EXTEND);
        onClose();
      },
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 999,
        position: 'fixed',
        left: buttonPosition ? `${buttonPosition.right + 10}px` : '2.5rem',
        top: buttonPosition ? `${buttonPosition.top - 48}px` : '50%',
        width: 'fit-content',
        transform: 'none',
        overflow: 'visible',
        '& .MuiBackdrop-root': { display: 'none' },
      }}
    >
      <Box>
        <ClickAwayListener onClickAway={onClose}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: '1.5rem',
              boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
              bgcolor: 'white',
              padding: '0.5rem 0',
              margin: '0',
              transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
            }}
          >
            {/* Modal Title */}
            <Box
              sx={{
                padding: '0.5rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                marginBottom: '0.5rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  color: 'rgba(0, 0, 0, 0.87)',
                }}
              >
                {intl.formatMessage({ id: 'Toolbar.extend' })}
              </Box>
            </Box>
            {/* Options List */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {options.map((option) => (
                <Box
                  component="div"
                  key={option.id}
                  onClick={option.action}
                  sx={{
                    display: 'grid',
                    gridTemplateRows: '2rem',
                    gridTemplateColumns: '28% 65%',
                    alignItems: 'center',
                    padding: '0.2rem 0.5rem',
                    fontSize: '1rem',
                    borderRadius: 0,
                    cursor: 'pointer',
                    backgroundColor: 'inherit',
                    color: activeDrawer === option.drawer ? primaryIconActive : primaryIconInactive,
                    width: '14rem',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: secondaryIconHover,
                    },
                    transition: 'background-color 0.2s ease-in-out',
                  }}
                >
                  <ImsIcon Icon={option.icon} style={menuIconStyle} />
                  {option.text}
                </Box>
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Box>
    </Modal>
  );
};

export default ExtendModal;
